import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { CircularProgress, Typography } from '@mui/material'

import { FlexDiv } from '../../components'
import { AppRoutes } from '../../helpers/Routes'
import { getShortFormDate } from '../../helpers/stringHelpers'
import { useInspectionHub } from '../../hooks/useInspectionHub'
import { RootState } from '../../store'
import { PropertyInspectionComplete } from '../../types/inspection'
import { InspectionAreaResults } from './InspectionAreaResults'
import { InspectionDetailShell } from './InspectionDetailShell'
import { countAreas, getLatestCompletedDate } from './inspectionUtils'

interface LocationState {
    propertyInspection?: PropertyInspectionComplete
}

const formatDateRange = (startDate: string, endDate: string) => {
    return `${getShortFormDate(startDate)} – ${getShortFormDate(endDate)}`
}

export const PropertyInspectionDetail = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { inspectionId } = useParams<{ inspectionId: string }>()
    const parsedId = inspectionId ? parseInt(inspectionId, 10) : NaN

    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const leaseId = selectedLease?.id
    const apartmentId = selectedLease?.apartment_id

    const { isLoading, getInspectionHub, getPropertyInspectionComplete } =
        useInspectionHub(leaseId, apartmentId)

    const stateEntry = (location.state as LocationState | null)?.propertyInspection
    const hubEntry =
        !Number.isNaN(parsedId)
            ? getPropertyInspectionComplete(parsedId)
            : undefined
    const entry = stateEntry ?? hubEntry

    useEffect(() => {
        if (!stateEntry && leaseId && apartmentId) {
            getInspectionHub()
        }
    }, [leaseId, apartmentId, stateEntry])

    const handleBack = () => navigate(AppRoutes.inspections)

    if (!leaseId || !apartmentId) {
        return (
            <InspectionDetailShell title="Inspection Results" onBack={handleBack}>
                <Typography color="textSecondary">
                    Select a lease to view inspection results.
                </Typography>
            </InspectionDetailShell>
        )
    }

    if (isLoading && !entry) {
        return (
            <InspectionDetailShell title="Inspection Results" onBack={handleBack}>
                <FlexDiv style={{ justifyContent: 'center', padding: 32, width: '100%' }}>
                    <CircularProgress size={40} />
                </FlexDiv>
            </InspectionDetailShell>
        )
    }

    if (!entry) {
        return (
            <InspectionDetailShell title="Inspection Results" onBack={handleBack}>
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                    Inspection not found or not available for your lease.
                </Typography>
            </InspectionDetailShell>
        )
    }

    const completedDate =
        entry.completed_date ?? getLatestCompletedDate(entry.detail)
    const metadata = [
        entry.inspection_type_name,
        formatDateRange(entry.start_date, entry.end_date),
        ...(completedDate
            ? [`Completed ${getShortFormDate(completedDate)}`]
            : []),
        `${countAreas(entry.detail)} area${countAreas(entry.detail) === 1 ? '' : 's'} inspected`,
    ]

    return (
        <InspectionDetailShell
            title={entry.name}
            status="complete"
            metadata={metadata}
            onBack={handleBack}
        >
            <InspectionAreaResults
                inspections={entry.detail}
                showVideos={false}
                defaultTab="issues"
                filterMode="flagged"
            />
        </InspectionDetailShell>
    )
}
